/**
 * Learning Analytics Utility
 * 
 * Tracks learning progress, improvement over time, subject mastery,
 * and adaptive learning effectiveness.
 * 
 * @module learningAnalytics
 */

// Lazy import analyticsCollector and logger to avoid initialization order issues
// import { analyticsCollector } from './analytics';
// import { logger } from './logger';

/**
 * Track learning progress for a subject
 * 
 * @param {string} subject - Subject identifier
 * @param {number} score - Score achieved
 * @param {number} accuracy - Accuracy percentage
 * @param {number} timeSpent - Time spent in milliseconds
 * @param {Object} [metadata] - Additional metadata
 */
export const trackLearningProgress = async (subject, score, accuracy, timeSpent, metadata = {}) => {
  // Lazy load modules to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  const { logger } = await import('./logger');
  
  analyticsCollector.trackEvent('learning_progress', {
    subject,
    score,
    accuracy,
    timeSpent,
    timestamp: Date.now(),
    ...metadata
  });

  logger.debug('Learning progress tracked:', { subject, score, accuracy, timeSpent });
};

/**
 * Track question answered
 * 
 * @param {string} subject - Subject identifier
 * @param {boolean} correct - Whether answer was correct
 * @param {number} timeSpent - Time spent in milliseconds
 * @param {number} attemptNumber - Attempt number (1 = first try)
 * @param {boolean} usedHint - Whether hint was used
 */
export const trackQuestionAnswered = async (subject, correct, timeSpent, attemptNumber = 1, usedHint = false) => {
  // Lazy load analyticsCollector to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  
  analyticsCollector.trackEvent('question_answered', {
    subject,
    correct,
    timeSpent,
    attemptNumber,
    usedHint,
    timestamp: Date.now()
  });
};

/**
 * Track subject mastery
 * 
 * @param {string} subject - Subject identifier
 * @param {number} masteryLevel - Mastery level (0-100)
 * @param {Object} [metadata] - Additional metadata
 */
export const trackSubjectMastery = async (subject, masteryLevel, metadata = {}) => {
  // Lazy load analyticsCollector to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  
  analyticsCollector.trackEvent('subject_mastery', {
    subject,
    masteryLevel,
    timestamp: Date.now(),
    ...metadata
  });
};

/**
 * Track improvement over time
 * 
 * @param {string} subject - Subject identifier
 * @param {number} previousScore - Previous score
 * @param {number} currentScore - Current score
 * @param {number} improvement - Improvement percentage
 */
export const trackImprovement = async (subject, previousScore, currentScore, improvement) => {
  // Lazy load analyticsCollector to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  
  analyticsCollector.trackEvent('learning_improvement', {
    subject,
    previousScore,
    currentScore,
    improvement,
    timestamp: Date.now()
  });
};

/**
 * Track adaptive learning effectiveness
 * 
 * @param {string} subject - Subject identifier
 * @param {string} difficulty - Difficulty level
 * @param {number} performance - Performance score
 * @param {boolean} adjusted - Whether difficulty was adjusted
 */
export const trackAdaptiveLearning = async (subject, difficulty, performance, adjusted) => {
  // Lazy load analyticsCollector to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  
  analyticsCollector.trackEvent('adaptive_learning', {
    subject,
    difficulty,
    performance,
    adjusted,
    timestamp: Date.now()
  });
};

/**
 * Get learning analytics summary
 * 
 * @param {string} [subject] - Optional subject filter
 * @returns {Object} Analytics summary
 */
export const getLearningAnalytics = async (subject = null) => {
  // Lazy load analyticsCollector to avoid initialization issues
  const { analyticsCollector } = await import('./analytics');
  const analyticsData = analyticsCollector.getAnalytics();
  const events = analyticsData.events || [];
  
  // Filter by subject if provided
  const filteredEvents = subject 
    ? events.filter(e => e.properties?.subject === subject)
    : events;
  
  // Calculate statistics
  const learningEvents = filteredEvents.filter(e => 
    e.eventName === 'learning_progress' || 
    e.eventName === 'question_answered'
  );
  
  const totalQuestions = learningEvents.filter(e => e.eventName === 'question_answered').length;
  const correctAnswers = learningEvents.filter(e => 
    e.eventName === 'question_answered' && e.properties?.correct === true
  ).length;
  
  const averageAccuracy = totalQuestions > 0 
    ? (correctAnswers / totalQuestions) * 100 
    : 0;
  
  const averageTime = learningEvents
    .filter(e => e.properties?.timeSpent)
    .reduce((sum, e) => sum + (e.properties.timeSpent || 0), 0) / 
    learningEvents.filter(e => e.properties?.timeSpent).length || 0;
  
  return {
    totalQuestions,
    correctAnswers,
    averageAccuracy,
    averageTime,
    learningEvents: learningEvents.length
  };
};

